import { useState, useEffect, useMemo } from "react";

// MUI
import { Box, Fade, Stack, SxProps } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";

// Components
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import ArrayDialog from "../../../common/ListColumnDialog/ArrayDialog";

// Hooks
import { useSmartTableColGenerator } from "@/Common/hooks/SmartTableColGenerator/useSmartTableColGenerator";

// Types
import { DataSmartTableProps } from "@/Layouts/components/types";
import { MappingDialogContent, MappingFilter } from "@/Common/types/meta/mappingFilters";

// Helpers
import { generateSx, getRowClassName, getRowId } from "@/Layouts/components/Tables/DataSmartTable/helpers";

// Constants
import { tableKeys } from "@/Constants/tableKeys";
import { BASE_TABLE_PROPS, DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT } from "@/Washouts/constants";

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
	noResultsOverlay: NoRowsOverlay,
};

const columnVisibilityModel = {
	_id: false,
};

const customProps: DataSmartTableProps["customProps"] = {
	...BASE_TABLE_PROPS,
	tableContainerClassName: "[&_.toolbar-actions]:!absolute [&_.toolbar-actions]:!right-[0px] [&_.toolbar-actions]:!top-[-8px]",
	tableClassName: {
		"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
		"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
	},
	aggregationFooterClassName: "aggregation-footer regular-footer",
	expandableRows: false,
	hide: false,
};

const noContentObject = {
	title: `No data to show`,
	className: "!text-montserrat",
};

interface IFnIDetailsTableProps {
	items: object[];
	isPaginating: boolean;
	mappingFilters: MappingFilter[];
}

export default function FnIDetailsTable(props: Readonly<IFnIDetailsTableProps>) {
	const { items = [], isPaginating, mappingFilters } = props ?? {};

	const [selectedFilter, setSelectedFilter] = useState<MappingFilter | null>(null);

	useEffect(() => setSelectedFilter(mappingFilters?.[0] ?? null), [mappingFilters]);

	const mappingCols = useMemo(() => selectedFilter?.columns ?? [], [selectedFilter]);

	const apiRef = useGridApiRef();
	const [sx, setSx] = useState<SxProps>({});

	useEffect(() => {
		if (!apiRef.current?.subscribeEvent) {
			return () => {};
		}
	}, [items]);

	useEffect(() => {
		setSx(generateSx(apiRef));
		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [mappingCols, apiRef]);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				filter: {
					hide: false,
				},
				search: {
					useQuickFilter: true,
				},
				customComponent: (
					<TableHeader
						isPaginating={isPaginating}
						selectedFilter={selectedFilter}
						filtersData={mappingFilters}
						onFilterChange={setSelectedFilter}
					/>
				),
			},
			basePopper: {
				sx: { zIndex: 1300 },
			},
			noRowsOverlay: noContentObject,
			noResultsOverlay: noContentObject,
		}),
		[isPaginating, selectedFilter]
	);

	const [modalContent, setModalContent] = useState<MappingDialogContent | null>(null);

	const augmentedMappingCols = useMemo(() => {
		return mappingCols.map((col) => {
			const isCustom = col.columnType === "CUSTOM" && col.variant === "ARRAY";

			if (!isCustom) {
				return col;
			}

			return {
				...col,
				modalClickHandler: (content: MappingDialogContent) => setModalContent(content),
			};
		});
	}, [mappingCols]);

	const { columns, pinnedColumns, aggregatedColumns } = useSmartTableColGenerator(augmentedMappingCols);

	return (
		<Stack className="!h-full relative">
			<DataSmartTable
				apiRef={apiRef}
				rows={items}
				columns={columns}
				pinnedColumns={pinnedColumns}
				aggregationModel={aggregatedColumns}
				getRowClassName={getRowClassName}
				getRowId={getRowId}
				autoHeight={false}
				headerHeight={DEFAULT_HEADER_HEIGHT}
				rowHeight={DEFAULT_ROW_HEIGHT}
				maxRows={19}
				filterStoreConfig={{ key: tableKeys.DATA_PAGES.FNI_MANAGEMENT_FI_DETAILS }}
				columnVisibilityModel={columnVisibilityModel}
				customProps={customProps}
				slotProps={slotProps}
				slots={slots}
				sx={sx}
				disableColumnMenu
				enableCustomScrollbars
				disableRowSelectionOnClick
			/>
			<ArrayDialog dialogContent={modalContent} handleOnCloseDialog={() => setModalContent(null)} />
		</Stack>
	);
}

// MARK:- Supporting Components
interface LoadingIndicatorProps {
	isLoading: boolean;
	label?: string;
	fadeDuration?: number;
	className?: string;
}

function LoadingIndicator(props: Readonly<LoadingIndicatorProps>) {
	const { isLoading, className, fadeDuration = 400, label = "Data is still loading..." } = props;

	return (
		<Fade timeout={fadeDuration} in={isLoading} unmountOnExit className={className}>
			<div>
				<LoadingSpinner label={label} className="items-center" />
			</div>
		</Fade>
	);
}

// MARK:- Table Header
interface TableHeaderProps {
	isPaginating: boolean;
	selectedFilter: MappingFilter | null;
	filtersData: MappingFilter[];
	onFilterChange: (filter: MappingFilter | null) => void;
}

function TableHeader(props: Readonly<TableHeaderProps>) {
	const { isPaginating, selectedFilter, filtersData, onFilterChange } = props;

	return (
		<Box className="pb-4">
			<SmartSelector
				selected={selectedFilter}
				selectBy="name"
				showBy="name"
				options={filtersData.sort((firstFilter, secondFilter) => firstFilter.name.localeCompare(secondFilter.name))}
				classes="!w-[200px]"
				onChangeAction={(value) => onFilterChange(value as MappingFilter)}
			/>

			{isPaginating && <LoadingIndicator isLoading={isPaginating} className="pt-2" />}
		</Box>
	);
}
